// UI COMPONENTS
import { Button, Container, Dropdown, Table } from "react-bootstrap";
// HOOKS & ICONS
import { BsArrowLeft, BsPencil, BsSearch, BsTrash } from "react-icons/bs";
import { Controller, useForm } from "react-hook-form";
// VALIDATION
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
// HELPERS
import { customStyles, formatDate, handleExportData } from "../../../../Utils/helpers/commonhelper";
import "./Designation.css";
import { toast } from "sonner";
import { DescriptionTooltip } from "../../../../Utils/helpers/descriptionTooltip/DescriptionTooltip";
import { TableSkeleton } from "../../../../Utils/skeletonLoader/TableSkeleton";
// API handlers
import {
  DeleteRequest,
  GetRequest,
  PutRequest,
} from "../../../../Utils/hooks/ApiHandler";
import DeleteModal from "../../../DeleteModal";
import useLocalStorageState from "use-local-storage-state";
import {
  useGetRequest,
  usePostRequest,
} from "../../../../Utils/hooks/QueryHandler";
import { useGetEssentials } from "../../../../Utils/hooks/useGetEssentials";
import { useEffect, useState } from "react";
import Select from "react-select";
import nodata from "../../../../Utils/assets/img/leads_no_data_found.png";
import masterImage from '../../../../Utils/assets/img/masterImage.png'
import { faFileExport, faFileImport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
//Form validation schema using Zod
const schema = z.object({
  name: z.string().min(1, "Designation is required"),
  description: z.string().optional(),
  status: z.enum(["Active", "Inactive"]).optional(),
});

export const Designation = () => {
  const { headers, extras, navigate, customerId } = useGetEssentials();

  //  Local states for managing designation data, errors, and loading state
  const [designations, setDesignations] = useState([]);
  const [editId, setEditId] = useState(null);

  // const [iserror, setisError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [designationToDelete, setDesignationToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  //React Hook Form setup
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      status: "Active",
    },
  });

  // rbac permission object
  const [PERMISSION, _] = useLocalStorageState('permission');
  // permission for master 
  const masterGrant = PERMISSION.setting.feature.master

  const designationImportFields = [
    { id: "title", label: "Designation Name", required: true },
    { id: "description", label: "Description", required: false },
  ];
  //Fetch designations when component mounts
  const {
    data: designationData,
    error,
    refetch: fetchDesignations,
    isLoading,

    isError,
  } = useGetRequest(
    `/masters/getAllDesignation/${customerId}`,
    "getDesignation",

    headers,
    extras
  );
  // Fetch departments on component mount
  useEffect(() => {
    if (designationData?.status === "success") {
      setDesignations(designationData.data);
    } else if (isError) {
      console.error("Error fetching designations:", error);
    }
  }, [designationData, isError]);

  //Add or update designation

  const { mutate: addDesignation } = usePostRequest(
    `/masters/addDesignation`,
    null,
    { headers },
    extras
  );

  const onSubmit = async (formData) => {
    if (editId === null) {
      if (!masterGrant?.isCreate) {
        toast.warning("You are not authorized for this action!")
        return
      }
    } else if (!masterGrant?.isEdit) {
      toast.warning("You are not authorized for this action!")
      return
    }
    const name = formData.name.trim();
    const description = formData.description?.trim() || "";
    const status = formData.status;
    //Check for duplicate
    const isDuplicate = designations.some(
      (d) => d.designation_name.trim() === name && d.id !== editId
    );
    if (isDuplicate) {
      toast.error("Designation name must be unique")
      return
    }

    try {
      const payload = {
        customer_id: customerId,
        designation_name: name,
        description,
        status,
      };

      let res;
      if (editId !== null) {
        payload.designation_id = editId;

        res = await PutRequest(
          `/masters/updateDesignation`,
          payload,
          { headers },
          extras
        );
      } else {
        addDesignation(payload, {
          onSuccess: (res) => {
            if (res?.status === "success") {
              toast.success(res.message || "Designation added successfully");
              fetchDesignations();

              reset({
                name: "",
                description: "",
                status: "Active",
              });
            } else {
              toast.error(res.message || "Failed to add designation");
            }
          },
          onError: (err) => {
            toast.error(err?.message || "Add designation request failed");
          },
        });
      }

      if (res?.status === "success") {
        await fetchDesignations();
        if (editId !== null) setEditId(null);
        reset({ name: "", description: "", status: "Active" });
        toast.success(res.message);
      } else if (editId !== null && res) {
        toast.error(res.message || "Failed to update designation");
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Something went wrong while saving.");
    } finally {
    }
  };

  //Set form data in edit mode
  const onEdit = async (item) => {
    try {
      const res = await GetRequest(
        `masters/getDetailsOfDesignation/${item.id}`,
        { headers },
        extras
      );

      if (res?.status === "success" && Array.isArray(res.data)) {
        const data = res.data[0];
        setEditId(item.id);
        reset({
          name: data.designation_name || "",
          description: data.description || "",
          status:
            data.status === "Active" || data.status === "Inactive"
              ? data.status
              : "Active",
        });
      } else {
        throw new Error(res.message || "Invalid response");
      }
    } catch (error) {
      console.error("Error loading details:", error);
      toast.error("Failed to load designation details");
    }
  };
  //Open delete confirmation modal
  const onDelete = (id) => {
    setDesignationToDelete(id);
    setShowDeleteModal(true);
  };
  //Handle confirmed delete
  const handleConfirmedDelete = async (id) => {
    try {
      const item = designations.find((d) => d.id === id);
      if (!item) return;
      setLoading(true)
      const res = await DeleteRequest(
        `masters/deleteDesignation/${id}`,
        { headers },
        extras
      );

      setShowDeleteModal(false);

      if (res?.status === "success") {
        await fetchDesignations();
        if (editId === id) {
          reset({ name: "", description: "", status: "Active" });
          setEditId(null);
        }
        setLoading(false)
        toast.success(res.message);
      } else {
        setLoading(false)
        toast.error(res.message);
      }
    } catch (error) {
      setLoading(false)
      console.error("Delete error:", error);
      toast.error("Something went wrong while deleting");
    }
  };

  return (
    <div className="container-fluid p-3 designation-container mb-5">
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleConfirmedDelete(designationToDelete)}
        message="Are you sure you want to delete this item?"
        isLoading={loading}
      />

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div style={{ width: "80%", paddingRight: "20px" }}>
          <h5 className="fw-semibold mb-1">Employee Designation</h5>
          <p className="text-muted small">
            Define and manage employee designations within an organization. This
            feature typically includes options to add, edit, and delete
            designations for better workforce organization.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button className="btn btn-default-outline" onClick={() => navigate(-1)}>
            <BsArrowLeft className="me-1" /> Back
          </Button>
          <Dropdown>
            <Dropdown.Toggle className=" btn btn-default no-arrow" style={{ display: "flex", alignItems: "center", paddingRight: "7px" }}>
              Action
              {/* <i className="bi bi-caret-down-fill d-inline-block ps-1" ><<i className="bx bx-chevron-down" style={{ fontSize: "21px" }}></i>/i> */}
              <i className="bx bx-chevron-down" style={{ fontSize: "21px" }}></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => {
                navigate("/importdata", {
                  state: {
                    module_name: "designation",
                    fieldData: [
                      {
                        title: "General Fields",
                        fields: designationImportFields.map((c) => c),
                      },
                    ],
                  },
                });
              }}><FontAwesomeIcon
                  icon={faFileImport}
                  style={{
                    width: "16px",
                    height: "16px",
                    marginRight: "9px",
                    color: "#5e5f60",
                  }}
                />{" "}
                Import Data</Dropdown.Item>
              <Dropdown.Item onClick={() => {
                handleExportData([], "designation", headers, extras);
              }}> <FontAwesomeIcon
                  icon={faFileExport}
                  style={{
                    width: "16px",
                    height: "16px",
                    marginRight: "9px",
                    color: "#5e5f60",
                  }}
                />{" "}
                Export Data</Dropdown.Item>
              {/* <Dropdown.Item>Copy from Other</Dropdown.Item>
              <Dropdown.Item>SVC Data</Dropdown.Item> */}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <hr
        className="mb-4"
        style={{ borderTop: "0.5px solid #6c757d", opacity: "0.3", marginTop: "0.7rem" }}
      />

      <div className="row">
        {/* Form Section */}
        <div className="col-md-4 mb-4">
          <div className="form-card">
            <div className="mb-3">
              <h6 className="fw-semibold mb-1">
                {editId ? "Edit Designation" : "Add Designation"}
              </h6>
              <p className="text-muted small mb-2">
                {editId
                  ? "Update the designation details below."
                  : "Add new designation here."}
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="form-container">
              <div className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <label className="form-label">
                  Enter designation name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Enter designation name"
                  {...register("name")}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name.message}</small>
                )}
              </div>

              <div className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <label className="form-label textareaLables">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  {...register("description")}
                />
              </div>

              <div className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <label className="form-label">Select designation status </label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      styles={customStyles}
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Inactive", label: "Inactive" },
                      ]}
                      classNamePrefix="react-select"
                      placeholder="Select status"
                      value={
                        field.value
                          ? { value: field.value, label: field.value }
                          : null
                      }
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption?.value); // object -> string
                      }}
                    />
                  )}
                />

                {errors.status && (
                  <small className="text-danger">{errors.status.message}</small>
                )}
              </div>

              <Button type="submit" className="w-100 btn btn-default">
                {editId ? "Update" : "Submit"}
              </Button>
            </form>
            <hr className="my-3" />
            <div className="text-center mt-4">
              <img
                src={masterImage}
                alt="illustration"
                className="img-fluid"
                style={{ maxHeight: "180px" }}
              />
              <div className="mt-2">
                <h6 className="fw-semibold mb-1">Employee Designation</h6>
                <p className="text-muted  mb-0 small ">
                  Define and manage employee designations within an
                  organization. This feature typically includes options to add,
                  edit, and delete designations for better workforce
                  organization.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}

        <div className="col-md-8">
          <div className="table-card">
            <div className="d-flex justify-content-between align-items-start align-items-md-center flex-column flex-md-row">
              <div>
                <h6 className="fw-semibold mb-1">Designations List</h6>
                <p className="text-muted small mb-2 mb-md-0">
                  List of current designations within your organization.
                </p>
              </div>
              {/* <div className="d-flex align-items-center gap-2 mt-1 mb-2 mt-md-0 border border-1 rounded-2 px-2"> */}
              <div className=" position-relative mt-1 mb-2 mt-md-0">
                <input
                  disabled={!masterGrant?.isView}
                  type="search"
                  className="form-control "
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    fontSize: "12px",
                    paddingLeft: "36px",
                    height: "35px",
                  }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    marginTop: "-26px",
                    zIndex: 1,
                    marginLeft: "13px",
                  }}
                ></i>
              </div>
            </div>

            {error ? (
              <div className="alert alert-danger" role="alert">
                {error?.message || "Something went wrong"}
              </div>
            ) : (
              <>
                {
                  masterGrant?.isView ?
                  <Container
                    fluid
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      height: "calc(100vh - 10rem)",
                      backgroundColor: "white",
                      padding: 0,
                      animation: "cardEntry 1s ease-out forwards",
                    }}
                    className="mt-3 p-0"
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.scrollbarColor = "#e0e0e0 white"

                      }}
                    >
                      <div
                        className="listStyleForProductandService container-table"
                        style={{
                          flex: designations?.length > 1 ? 1 : "auto",
                          overflow: "auto",
                          width: "100%",
                          maxHeight: designations?.length > 1 ? "100%" : "auto",
                        }}
                      >
                        <Table
                          style={{
                            width: "100%",
                            tableLayout: "auto",
                            backgroundColor: "white",
                            marginBottom: "0px",
                          }}
                        >
                          <thead style={{ zIndex: "10" }}>
                            <tr>
                              <th>S.No</th>
                              <th>NAME</th>
                              <th>DESCRIPTION</th>
                              <th>CREATED ON</th>
                              <th>STATUS</th>
                              <th>ACTION</th>
                            </tr>
                          </thead>

                          <tbody>
                            {isLoading ? <TableSkeleton columnCount={6} /> :
                              <>
                                {designations
                                  ?.filter((d) =>
                                    (d.designation_name || "")
                                      .toLowerCase()
                                      .includes(searchTerm.trim().toLowerCase())
                                  )
                                  .map((d, idx) => (
                                    <tr key={d?.id}>
                                      <td>{idx + 1}</td>
                                      <td className="td_position">
                                        {/* {d.designation_name} */}
                                        <DescriptionTooltip
                                          text={d.designation_name}
                                          maxLength={20}
                                        />

                                      </td>
                                      <td>
                                        <DescriptionTooltip
                                          text={d.description}
                                          maxLength={20}
                                        />
                                      </td>
                                      <td>{formatDate(d?.created_at)}</td>
                                      <td>
                                        {/* <div
                                            className={`badge1 ${d.status === "Active"
                                              ? "badge-active"
                                              : "badge-inactive"
                                              }`}
                                          >
                                            {d.status}
                                          </div> */}
                                        <div
                                          className={` ${d.status === "Active"
                                            ? "btn btn-sm btn-primary-light"
                                            : "btn btn-sm btn-danger-light"
                                            }`}
                                        >
                                          {d.status}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="d-flex gap-2">
                                          {/* <Button
                                              size="sm"
                                              variant="outline-primary"
                                              onClick={() => {
                                                if (!masterGrant?.isEdit) {
                                                  toast.warning("You are not authorized for this action!")
                                                  return
                                                }
                                                onEdit(d)
                                              }
                                              }
                                              title="Edit"
                                            >
                                              <BsPencil />
                                            </Button> */}
                                          <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => {
                                              if (!masterGrant?.isEdit) {
                                                toast.warning("You are not authorized for this action!")
                                                return
                                              }
                                              onEdit(d)
                                            }}
                                            title="Edit"
                                          >
                                            <BsPencil />
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline-danger"
                                            title="Delete"
                                            onClick={() => {
                                              if (!masterGrant?.isDelete) {
                                                toast.warning("You are not authorized for this action!")
                                                return
                                              }
                                              onDelete(d.id)
                                            }
                                            }
                                          >
                                            <BsTrash />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                {/*  if no data found on search then it will run  */}
                                {designations.filter((d) =>
                                  (d.designation_name || "")
                                    .toLowerCase()
                                    .includes(searchTerm.trim().toLowerCase())
                                ).length === 0 && (
                                    <td
                                      colSpan="14"
                                      style={{ textAlign: "center", color: "gray" }}
                                    >
                                      <div
                                        className="d-flex justify-content-center align-items-center"
                                        style={{ position: "absolute", left: " 30%" }}
                                      >
                                        <img
                                          src={nodata}
                                          alt=""
                                          style={{ width: "17rem", marginTop: "0" }}
                                        />
                                      </div>
                                    </td>
                                  )}
                              </>
                            }
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </Container>
                  :
                  <>You are not authorized to view Designation listing!</>
                }

              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
